import type { Plan } from '@/services/api/api.types'

import { FREE_PLAN_PRICE } from './onboardingConstants'

/**
 * Gets the free plan from a list of plans
 */
export function getFreePlan(plans: Plan[]): Plan | undefined {
  return plans.find(plan => plan.priceDollars === FREE_PLAN_PRICE)
}

/**
 * Gets the free plan credits amount
 */
export function getFreePlanCredits(plans: Plan[]): number {
  const freePlan = getFreePlan(plans)
  return freePlan?.creditsPerBundle ?? 0
}

/**
 * Determines if a plan is eligible for the one-time credit bonus
 */
export function isPlanEligibleForBonus(plan: Plan): boolean {
  return plan.priceDollars > FREE_PLAN_PRICE
}

/**
 * Gets the bottom section text for plan cards
 */
export function getPlanBottomSectionText(plan: Plan, allPlans: Plan[]): string {
  if (plan.priceDollars === FREE_PLAN_PRICE) {
    return 'Free trial'
  }
  const bonusCredits = getFreePlanCredits(allPlans)
  return `+ ${bonusCredits} bonus credits`
}

/**
 * Formats price for display
 */
export function formatPlanPrice(dollars: number): string {
  return dollars === FREE_PLAN_PRICE ? 'Free' : `$${Math.round(dollars)}`
}

/**
 * Gets the billing period text
 */
export function getBillingPeriod(plan: Plan, autoRefillEnabled: boolean): string {
  if (plan.priceDollars === FREE_PLAN_PRICE) return ''
  return autoRefillEnabled ? '/ per refill' : '/ one-time'
}

/**
 * Formats credits for display
 */
export function formatCredits(credits: number): string {
  return credits.toLocaleString()
}

/**
 * Calculates the cost per credit for a plan
 */
export function getCostPerCredit(plan: Plan): number {
  if (plan.priceDollars === FREE_PLAN_PRICE || plan.creditsPerBundle === 0) {
    return 0
  }
  return plan.priceDollars / plan.creditsPerBundle
}

/**
 * Formats cost per credit for display
 */
export function formatCostPerCredit(plan: Plan): string {
  const costPerCredit = getCostPerCredit(plan)
  if (costPerCredit === 0) {
    return 'No charge'
  }
  return `$${costPerCredit.toFixed(2)} per credit`
}

/**
 * Calculates the auto-refill threshold for a plan (10% of credits)
 */
export function calculateAutoRefillThreshold(plan: Plan): number {
  return Math.ceil(plan.creditsPerBundle * 0.1)
}
